/**
 * SPEC-15 seed — the Command Centre's API client.
 *
 * One fetch wrapper: bearer token from localStorage, one silent refresh on
 * a 401, envelope unwrapping, typed errors surfaced as messages. The API
 * base is compile-time configurable; production points at the live worker.
 */

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  'https://synanalytics-api.plabon495.workers.dev';
const V1 = `${API_BASE}/api/v1`;
/** The address an AI tool connects to (C8 MCP server). */
export const MCP_SERVER_URL = `${API_BASE}/mcp`;

interface Envelope<T> {
  data?: T;
  error?: { code?: string; message?: string };
}

let accessToken: string | null = localStorage.getItem('synops_access');
let refreshToken: string | null = localStorage.getItem('synops_refresh');

export function isAuthenticated(): boolean {
  return accessToken !== null;
}

export function signOut(): void {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('synops_access');
  localStorage.removeItem('synops_refresh');
}

/** C7 — a session that arrived another way (a Google sign-in grant). */
export function adoptSession(access: string, refresh: string): void {
  storeTokens(access, refresh);
}

function storeTokens(access: string, refresh: string | null): void {
  accessToken = access;
  localStorage.setItem('synops_access', access);
  if (refresh) {
    refreshToken = refresh;
    localStorage.setItem('synops_refresh', refresh);
  }
}

// A refresh token works once (it is rotated), so concurrent 401s must share
// ONE refresh: a second request spending the same token is refused and
// signed the person out whenever several calls expired together.
let refreshing: Promise<boolean> | null = null;

function tryRefresh(): Promise<boolean> {
  refreshing ??= refreshOnce().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function refreshOnce(): Promise<boolean> {
  // Another tab may have rotated it already: use the newest one saved.
  refreshToken = localStorage.getItem('synops_refresh') ?? refreshToken;
  if (!refreshToken) return false;
  const response = await fetch(`${V1}/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!response.ok) return false;
  const body = (await response.json()) as Envelope<{
    access_token?: string;
    refresh_token?: string;
  }>;
  if (!body.data?.access_token) return false;
  storeTokens(body.data.access_token, body.data.refresh_token ?? null);
  return true;
}

export class ApiError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

async function call<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
  const response = await fetch(`${V1}${path}`, { ...init, headers });
  if (response.status === 401 && !retried && (await tryRefresh())) {
    return call<T>(path, init, true);
  }
  const body = (await response.json().catch(() => ({}))) as Envelope<T>;
  if (!response.ok) {
    throw new ApiError(
      body.error?.code ?? `HTTP_${response.status}`,
      body.error?.message ?? `Request failed (${response.status}).`,
    );
  }
  return body.data as T;
}

// ── Auth + organisation ────────────────────────────────────────────────────

export interface SessionUser {
  id: string;
  email: string;
}

/**
 * Signs in. With a second factor on, the API answers with a challenge instead
 * of a session — no tokens are stored until the code is verified.
 */
export async function signIn(
  email: string,
  password: string,
  turnstileToken: string,
): Promise<{ user: SessionUser } | { challengeToken: string }> {
  const data = await call<{
    access_token?: string;
    refresh_token?: string;
    user: SessionUser;
    mfa_required?: boolean;
    challenge_token?: string;
  }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, turnstile_token: turnstileToken }),
  });
  if (data.mfa_required && data.challenge_token) return { challengeToken: data.challenge_token };
  storeTokens(data.access_token as string, data.refresh_token as string);
  return { user: data.user };
}

/** Completes a sign-in that was answered with a second-factor challenge. */
export async function verifySignInCode(challengeToken: string, code: string): Promise<SessionUser> {
  const data = await call<{ access_token: string; refresh_token: string; user?: SessionUser }>(
    '/auth/mfa/verify',
    {
      method: 'POST',
      body: JSON.stringify({ challenge_token: challengeToken, code }),
    },
  );
  storeTokens(data.access_token, data.refresh_token);
  return data.user ?? { id: 'session', email: '' };
}

/**
 * Asks for a reset email. The API answers identically whether or not the
 * account exists, so the caller must never branch on the result — doing so
 * would turn this into an account-enumeration oracle.
 */
export async function requestPasswordReset(email: string, turnstileToken: string): Promise<void> {
  await call<{ message: string }>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email, turnstile_token: turnstileToken }),
  });
}

/** Completes a reset with the emailed token. Errors carry the API's own wording. */
export async function resetPassword(
  token: string,
  password: string,
  turnstileToken: string,
): Promise<void> {
  await call<{ message: string }>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password, turnstile_token: turnstileToken }),
  });
}

export interface Organisation {
  id: string;
  name: string;
  permissions: string[];
  /** 'TRIAL' while on the free trial; null for workspaces made before self-serve sign-up. */
  plan?: string | null;
  trial_ends_at?: string | null;
}

export async function myOrganisation(): Promise<Organisation> {
  const data = await call<{ organisations: Organisation[] }>('/me/organisations');
  const organisation = data.organisations[0];
  if (!organisation) throw new ApiError('NO_ORGANISATION', 'This account has no organisation.');
  return organisation;
}

const org = (organisationId: string, path: string) => `/organisations/${organisationId}${path}`;

// ── Two-step verification ──────────────────────────────────────────────────
// Mandatory on every account: until it is on, the API answers 403 MFA_REQUIRED
// to everything but these calls, so the app asks for it straight after sign-in.

export const twoStepEnabled = () =>
  call<{ mfa_enabled: boolean }>('/me').then((data) => data.mfa_enabled);

export const beginTwoStep = () =>
  call<{ secret: string; uri: string }>('/auth/mfa/setup', { method: 'POST', body: '{}' });

export const confirmTwoStep = (code: string) =>
  call<{ enabled: boolean; recovery_codes: string[] }>('/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  }).then((data) => data.recovery_codes);

// ── Reports (R1) ───────────────────────────────────────────────────────────
// The report screens keep their calls in reports/reports-api.ts; they reach
// the shared, token-refreshing transport through these two exports.
export const apiCall = call;

/** A file from the API (e.g. a PDF export), with the same sign-in and one refresh retry as apiCall. */
export async function apiDownload(path: string, retried = false): Promise<Blob> {
  const headers = new Headers();
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
  const response = await fetch(`${V1}${path}`, { headers });
  if (response.status === 401 && !retried && (await tryRefresh())) return apiDownload(path, true);
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as Envelope<unknown>;
    throw new ApiError(
      body.error?.code ?? `HTTP_${response.status}`,
      body.error?.message ?? `Download failed (${response.status}).`,
    );
  }
  return response.blob();
}
/** C8 — a multipart upload (a client file), with the same sign-in and one refresh retry. */
export async function apiUpload<T>(path: string, form: FormData, retried = false): Promise<T> {
  const headers = new Headers();
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
  const response = await fetch(`${V1}${path}`, { method: 'POST', headers, body: form });
  if (response.status === 401 && !retried && (await tryRefresh()))
    return apiUpload<T>(path, form, true);
  const body = (await response.json().catch(() => ({}))) as Envelope<T>;
  if (!response.ok)
    throw new ApiError(
      body.error?.code ?? `HTTP_${response.status}`,
      body.error?.message ?? `Upload failed (${response.status}).`,
    );
  return body.data as T;
}
export const orgPath = org;
